import React, { useState, useCallback, useEffect } from "react";
import debounce from "lodash.debounce";
import { useSelector } from "react-redux";
import { selectIsOperatorSession } from "../../store/slices/authUserSlice";

interface BlueprintSettingsPanelProps {
  audience: string;
  additionalDetails: string;
  operatorNotes: string;
  firstPersonVoice: string;
  saveBlueprintSettings: (audience: string, additionalDetails: string, operatorNotes: string, firstPersonVoice: string) => Promise<any>;
}

const BlueprintSettingsPanel: React.FC<BlueprintSettingsPanelProps> = ({
  audience,
  additionalDetails,
  operatorNotes,
  firstPersonVoice,
  saveBlueprintSettings,
}) => {
  // Operators only: this drives how the AI writes, so a client editing it would
  // change their own copy's voice without knowing what it does.
  const isOperator = useSelector(selectIsOperatorSession);
  const [audienceValue, setAudienceValue] = useState(audience);
  const [additionalDetailsValue, setAdditionalDetailsValue] = useState(additionalDetails);
  const [operatorNotesValue, setOperatorNotesValue] = useState(operatorNotes);
  const [firstPersonVoiceValue, setFirstPersonVoiceValue] = useState(firstPersonVoice);

  useEffect(() => {
    setAudienceValue(audience);
    setAdditionalDetailsValue(additionalDetails);
    setOperatorNotesValue(operatorNotes);
    setFirstPersonVoiceValue(firstPersonVoice);
  }, [audience, additionalDetails, operatorNotes, firstPersonVoice]);

  const debouncedSaveSettings = useCallback(
    debounce((newAudience: string, newAdditionalDetails: string, newOperatorNotes: string, newFirstPersonVoice: string) => {
      saveBlueprintSettings(newAudience, newAdditionalDetails, newOperatorNotes, newFirstPersonVoice);
    }, 500),
    [saveBlueprintSettings]
  );

  const handleAudienceChange = (newAudience: string) => {
    setAudienceValue(newAudience);
    debouncedSaveSettings(newAudience, additionalDetailsValue, operatorNotesValue, firstPersonVoiceValue);
  };

  const handleAdditionalDetailsChange = (newAdditionalDetails: string) => {
    setAdditionalDetailsValue(newAdditionalDetails);
    debouncedSaveSettings(audienceValue, newAdditionalDetails, operatorNotesValue, firstPersonVoiceValue);
  };

  const handleOperatorNotesChange = (newOperatorNotes: string) => {
    setOperatorNotesValue(newOperatorNotes);
    debouncedSaveSettings(audienceValue, additionalDetailsValue, newOperatorNotes, firstPersonVoiceValue);
  };

  const handleFirstPersonVoiceChange = (newFirstPersonVoice: string) => {
    setFirstPersonVoiceValue(newFirstPersonVoice);
    debouncedSaveSettings(audienceValue, additionalDetailsValue, operatorNotesValue, newFirstPersonVoice);
  };

  return (
    <div style={{
      height: "100vh",
      overflowY: "scroll",
    }}>
      <h1>Edit Prompts</h1>

      <p className="blueprint-section-prop">
        <b style={{ textAlign: "left" }}>Audience:</b>
      </p>
      <div style={{ padding: "0 16px" }}>
        <textarea
          value={audienceValue}
          onChange={(e) => handleAudienceChange(e.target.value)}
          placeholder="Describe your target audience..."
          rows={8}
          cols={30}
          className="feedback-textarea-input"
          style={{ marginBottom: "20px" }}
        />
      </div>

      <p className="blueprint-section-prop">
        <b style={{ textAlign: "left" }}>Additional Details:</b>
      </p>
      <p style={{ padding: "0 16px", margin: "0 0 8px", fontSize: "13px", color: "#6b7280" }}>
        Use this for voice and tone guidance, writing style preferences, or any other instructions for the AI.
      </p>
      <div style={{ padding: "0 16px" }}>
        <textarea
          value={additionalDetailsValue}
          onChange={(e) => handleAdditionalDetailsChange(e.target.value)}
          placeholder="Add examples, writing style, preamble, prompt, or other notes..."
          rows={24}
          cols={30}
          className="feedback-textarea-input"
          style={{ marginBottom: "20px" }}
        />
      </div>

      {isOperator && (
        <>
          <p className="blueprint-section-prop">
            <b style={{ textAlign: "left" }}>First-person voice:</b>
          </p>
          <p style={{ padding: "0 16px", margin: "0 0 8px", fontSize: "13px", color: "#6b7280" }}>
            The name this newsletter is written as. Sections built from this client's own
            posts, podcasts or interviews are written in this person's voice instead of
            being reported about them. Leave it blank and it gets filled in
            automatically from the sending name or the template greeting.
          </p>
          <div style={{ padding: "0 16px", marginBottom: "20px" }}>
            <input
              type="text"
              value={firstPersonVoiceValue}
              onChange={(e) => handleFirstPersonVoiceChange(e.target.value)}
              placeholder="e.g. Evan Kozliner"
              className="feedback-textarea-input"
              style={{ width: "100%" }}
            />
          </div>
        </>
      )}

      <p className="blueprint-section-prop">
        <b style={{ textAlign: "left" }}>Operator Notes:</b>
      </p>
      <p style={{ padding: "0 16px", margin: "0 0 8px", fontSize: "13px", color: "#6b7280" }}>
        These are notes for people reading this blueprint. They are not used by the AI.
      </p>
      <div style={{ padding: "0 16px", marginBottom: "20px" }}>
        <textarea
          value={operatorNotesValue}
          onChange={(e) => handleOperatorNotesChange(e.target.value)}
          placeholder="Add internal notes..."
          rows={12}
          cols={30}
          className="feedback-textarea-input"
          style={{ marginBottom: "20px" }}
        />
      </div>
    </div>
  );
};

export default BlueprintSettingsPanel;
